import type { CurrencyCode } from 'client-localisation/constants/currencies';

export type DeliveryZoneCodeId = keyof DeliveryZoneCodes;
export type CountryGroupCode = 'UNITED_KINGDOM' | 'OTHER';
export type DeliveryZoneCodes = typeof deliveryZoneCodes;
export type DeliveryZoneCode = DeliveryZoneCodes[keyof DeliveryZoneCodes];

export type Country = {
  [DeliveryZoneId in keyof DeliveryZoneCodes]: {
    countryCode: string;
    currency: CurrencyCode;
    deliveryZoneCode: DeliveryZoneCodes[DeliveryZoneId];
    deliveryZoneId: DeliveryZoneId;
    groupCode: CountryGroupCode;
    name: string;
  };
}[keyof DeliveryZoneCodes];

export interface CountryGroup {
  groupCode: CountryGroupCode;
  name: string;
}

export const deliveryZoneCodes = {
  1: 'MAINLAND_UK',
  2: 'HIGHLANDS_ISLANDS_OF_SCOTLAND',
  3: 'NORTHERN_IRELAND_ISLE_OF_MAN_ISLES_OF_SCILLY_CHANNEL_ISLANDS',
  4: 'WESTERN_EUROPE',
  5: 'REST_OF_EUROPE',
  6: 'USA_CANADA',
  7: 'ASIA_AUSTRALASIA',
  8: 'REST_OF_THE_WORLD',
  9: 'NORTHERN_IRELAND',
  10: 'OTHER_UK_ISLANDS',
} as const;

export const deliveryZoneCodeValues = Object.values(deliveryZoneCodes);

/**
 * The zone to assume when a visitor's own is unknown.
 *
 * Named rather than written as `deliveryZoneCodes[1]` at each call site. The keys
 * above are a numbering that is **not** shared with the systems that consume these
 * codes: `delivery-settings` seeds 3 as `NORTHERN_IRELAND` and 4 as
 * `OTHER_UK_ISLANDS`, where this map has 3 as
 * `NORTHERN_IRELAND_ISLE_OF_MAN_ISLES_OF_SCILLY_CHANNEL_ISLANDS` and 4 as
 * `WESTERN_EUROPE`. So an index is not just terse, it is the kind of thing that
 * gets copied to a neighbouring number and quietly means somewhere else.
 *
 * Mainland UK is the right default because it is what the listings API assumes for
 * a request that names no zone. Naming it here keeps that assumption in one place
 * rather than restated as a literal wherever a request is built.
 */
export const MAINLAND_UK_DELIVERY_ZONE = deliveryZoneCodes[1];

export const ukDeliveryZones: DeliveryZoneCode[] = [
  'MAINLAND_UK',
  'HIGHLANDS_ISLANDS_OF_SCOTLAND',
  'NORTHERN_IRELAND',
  'NORTHERN_IRELAND_ISLE_OF_MAN_ISLES_OF_SCILLY_CHANNEL_ISLANDS',
  'OTHER_UK_ISLANDS',
];

export const countries: Country[] = [
  {
    countryCode: 'GB',
    deliveryZoneId: 1,
    deliveryZoneCode: 'MAINLAND_UK',
    name: 'Mainland UK',
    groupCode: 'UNITED_KINGDOM',
    currency: 'GBP',
  },
  {
    countryCode: 'GB',
    deliveryZoneId: 2,
    deliveryZoneCode: 'HIGHLANDS_ISLANDS_OF_SCOTLAND',
    name: 'Highlands & Islands of Scotland',
    groupCode: 'UNITED_KINGDOM',
    currency: 'GBP',
  },
  {
    countryCode: 'GB',
    deliveryZoneId: 9,
    deliveryZoneCode: 'NORTHERN_IRELAND',
    name: 'Northern Ireland',
    groupCode: 'UNITED_KINGDOM',
    currency: 'GBP',
  },
  {
    countryCode: 'GB',
    deliveryZoneId: 10,
    deliveryZoneCode: 'OTHER_UK_ISLANDS',
    name: 'Other UK Islands',
    groupCode: 'UNITED_KINGDOM',
    currency: 'GBP',
  },
  {
    countryCode: 'AX',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Åland Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AL',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Albania',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'DZ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Algeria',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AS',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'American Samoa',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AD',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Andorra',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'AO',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Angola',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AI',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Anguilla',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AQ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Antarctica',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AG',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Antigua and Barbuda',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AR',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Argentina',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AM',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Armenia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AW',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Aruba',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AU',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Australia',
    groupCode: 'OTHER',
    currency: 'AUD',
  },
  {
    countryCode: 'AT',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Austria',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'AZ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Azerbaijan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BS',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Bahamas',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BH',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Bahrain',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BD',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Bangladesh',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BB',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Barbados',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BE',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'Belgium',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'BZ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Belize',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BJ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Benin',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Bermuda',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BT',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Bhutan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BO',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Bolivia, Plurinational State of',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BQ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Bonaire, Sint Eustatius and Saba',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BA',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Bosnia and Herzegovina',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BW',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Botswana',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BV',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Bouvet Island',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BR',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Brazil',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'IO',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'British Indian Ocean Territory',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BN',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Brunei Darussalam',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BG',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Bulgaria',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BF',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Burkina Faso',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BI',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Burundi',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'KH',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Cambodia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Cameroon',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CA',
    deliveryZoneId: 6,
    deliveryZoneCode: 'USA_CANADA',
    name: 'Canada',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CV',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Cape Verde',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'KY',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Cayman Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TD',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Chad',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CL',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Chile',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CN',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'China',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CX',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Christmas Island',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CC',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Cocos (Keeling) Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CO',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Colombia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'KM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Comoros',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CG',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Congo',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CK',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Cook Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CR',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Costa Rica',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'HR',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Croatia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CW',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Curaçao',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CY',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Cyprus',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CZ',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Czech Republic',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'DK',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'Denmark',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'DJ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Djibouti',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'DM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Dominica',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'DO',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Dominican Republic',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'EC',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Ecuador',
    groupCode: 'OTHER',
    currency: 'USD',
  },
  {
    countryCode: 'EG',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Egypt',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SV',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'El Salvador',
    groupCode: 'OTHER',
    currency: 'USD',
  },
  {
    countryCode: 'GQ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Equatorial Guinea',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'EE',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Estonia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'ET',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Ethiopia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'FK',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Falkland Islands (Malvinas)',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'FO',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Faroe Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'FJ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Fiji',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'FI',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Finland',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'FR',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'France',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'GF',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'French Guiana',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PF',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'French Polynesia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TF',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'French Southern Territories',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GA',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Gabon',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Gambia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GE',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Georgia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'DE',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'Germany',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'GH',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Ghana',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GI',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Gibraltar',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GR',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'Greece',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'GL',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Greenland',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GD',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Grenada',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GP',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Guadeloupe',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GU',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Guam',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GT',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Guatemala',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GG',
    deliveryZoneId: 10,
    deliveryZoneCode: 'OTHER_UK_ISLANDS',
    name: 'Guernsey',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GW',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Guinea-Bissau',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GY',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Guyana',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'HT',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Haiti',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'HM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Heard Island and McDonald Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'VA',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Holy See (Vatican City State)',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'HN',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Honduras',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'HK',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Hong Kong',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'HU',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Hungary',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'IS',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Iceland',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'IN',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'India',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'ID',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Indonesia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'IE',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'Ireland',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'IM',
    deliveryZoneId: 10,
    deliveryZoneCode: 'OTHER_UK_ISLANDS',
    name: 'Isle of Man',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'IL',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Israel',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'IT',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'Italy',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'JM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Jamaica',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'JP',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Japan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'JE',
    deliveryZoneId: 10,
    deliveryZoneCode: 'OTHER_UK_ISLANDS',
    name: 'Jersey',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'JO',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Jordan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'KZ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Kazakhstan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'KE',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Kenya',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'KI',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Kiribati',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'KR',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Korea, Republic of',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'KW',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Kuwait',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'KG',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Kyrgyzstan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'LA',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: "Lao People's Democratic Republic",
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'LV',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Latvia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'LS',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Lesotho',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'LI',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Liechtenstein',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'LT',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Lithuania',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'LU',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'Luxembourg',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'MO',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Macao',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MK',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Macedonia, Republic of',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MG',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Madagascar',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MW',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Malawi',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MY',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Malaysia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MV',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Maldives',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'ML',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Mali',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MT',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Malta',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MH',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Marshall Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MQ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Martinique',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MR',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Mauritania',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MU',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Mauritius',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'YT',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Mayotte',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MX',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Mexico',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'FM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Micronesia, Federated States of',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MD',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Moldova, Republic of',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MN',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Mongolia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'ME',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Montenegro',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'MS',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Montserrat',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MA',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Morocco',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MZ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Mozambique',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NA',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Namibia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NR',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Nauru',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NP',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Nepal',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NL',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'Netherlands',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'AN',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Netherlands Antilles',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NC',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'New Caledonia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NZ',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'New Zealand',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NI',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Nicaragua',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NE',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Niger',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NG',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Nigeria',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NU',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Niue',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NF',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Norfolk Island',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MP',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Northern Mariana Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'NO',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Norway',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'OM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Oman',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PK',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Pakistan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PW',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Palau',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PS',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Palestine, State of',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PA',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Panama',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PG',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Papua New Guinea',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PY',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Paraguay',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PE',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Peru',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PH',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Philippines',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PN',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Pitcairn',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PL',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Poland',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PT',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'Portugal',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'PR',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Puerto Rico',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'QA',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Qatar',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'RE',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Réunion',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'RO',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Romania',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'RW',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Rwanda',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'BL',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Saint Barthélemy',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SH',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Saint Helena, Ascension and Tristan da Cunha',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'KN',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Saint Kitts and Nevis',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'LC',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Saint Lucia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'MF',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Saint Martin (French part)',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'PM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Saint Pierre and Miquelon',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'VC',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Saint Vincent and the Grenadines',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'WS',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Samoa',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SM',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'San Marino',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'ST',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Sao Tome and Principe',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SA',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Saudi Arabia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SN',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Senegal',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'RS',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Serbia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SC',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Seychelles',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SL',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Sierra Leone',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SG',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Singapore',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SX',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Sint Maarten (Dutch part)',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SK',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Slovakia',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'SI',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Slovenia',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'SB',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Solomon Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'ZA',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'South Africa',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GS',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'South Georgia and the South Sandwich Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'ES',
    deliveryZoneId: 4,
    deliveryZoneCode: 'WESTERN_EUROPE',
    name: 'Spain',
    groupCode: 'OTHER',
    currency: 'EUR',
  },
  {
    countryCode: 'LK',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Sri Lanka',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SR',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Suriname',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SJ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Svalbard and Jan Mayen',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SZ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Swaziland',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'SE',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Sweden',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'CH',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Switzerland',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TW',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Taiwan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TJ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Tajikistan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TZ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Tanzania, United Republic of',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TH',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Thailand',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TL',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Timor-Leste',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TG',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Togo',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TK',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Tokelau',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TO',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Tonga',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TT',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Trinidad and Tobago',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TN',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Tunisia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TR',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Turkey',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Turkmenistan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TC',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Turks and Caicos Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'TV',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Tuvalu',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'UG',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Uganda',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'UA',
    deliveryZoneId: 5,
    deliveryZoneCode: 'REST_OF_EUROPE',
    name: 'Ukraine',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'AE',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'United Arab Emirates',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'GB',
    deliveryZoneId: 1,
    deliveryZoneCode: 'MAINLAND_UK',
    name: 'United Kingdom',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'US',
    deliveryZoneId: 6,
    deliveryZoneCode: 'USA_CANADA',
    name: 'United States',
    groupCode: 'OTHER',
    currency: 'USD',
  },
  {
    countryCode: 'UM',
    deliveryZoneId: 6,
    deliveryZoneCode: 'USA_CANADA',
    name: 'United States Minor Outlying Islands',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'UY',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Uruguay',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'UZ',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Uzbekistan',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'VU',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Vanuatu',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'VN',
    deliveryZoneId: 7,
    deliveryZoneCode: 'ASIA_AUSTRALASIA',
    name: 'Viet Nam',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'VG',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Virgin Islands, British',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'VI',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Virgin Islands, U.S.',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'WF',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Wallis and Futuna',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'EH',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Western Sahara',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
  {
    countryCode: 'ZM',
    deliveryZoneId: 8,
    deliveryZoneCode: 'REST_OF_THE_WORLD',
    name: 'Zambia',
    groupCode: 'OTHER',
    currency: 'GBP',
  },
];
